import React, { useEffect, useState } from "react";

import {
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Box, Drawer, IconButton, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import { topBarHeight } from "./styles";
import {
  DEV_TOOLS_OVERLAY_OPEN_EVENT,
  DevToolsOverlayTab,
} from "helpers/dev-tools-overlay-bridge";
import SupportServiceForm from "pages/dev-tools";

// Matches the sidebar's own collapse chevron (navigation.tsx) for visual consistency.
const CHEVRON_SIZE = 26;
const CHEVRON_TOP = topBarHeight + 22;
const DRAWER_MARGIN = 120;
const DRAWER_WIDTH = `calc(100vw - ${DRAWER_MARGIN}px)`;

/**
 * Global Dev Tools overlay, opened from anywhere in the app via Ctrl/Cmd+Shift+`
 * (backquote). Layers the same request/response/history UI as the /dev-tools
 * route on top of the current page, so the user doesn't lose their place.
 */
const DevToolsOverlay: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<DevToolsOverlayTab>("builder");

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === "Backquote") {
        e.preventDefault();
        setInitialTab("builder");
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleOpenRequest = (e: Event) => {
      setInitialTab(
        (e as CustomEvent<{ tab?: DevToolsOverlayTab }>).detail?.tab ??
          "builder",
      );
      setOpen(true);
    };
    window.addEventListener(DEV_TOOLS_OVERLAY_OPEN_EVENT, handleOpenRequest);
    return () =>
      window.removeEventListener(
        DEV_TOOLS_OVERLAY_OPEN_EVENT,
        handleOpenRequest,
      );
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      // Just enough to clear the persistent top nav bar (theme.zIndex.drawer
      // + 1/+2 in navigation.tsx), and deliberately still below
      // theme.zIndex.modal (1300). Any MUI Select/Autocomplete/Menu/Dialog
      // rendered inside this overlay with no z-index override of its own
      // (e.g. MethodSelector's Autocomplete) defaults to exactly
      // theme.zIndex.modal, portaled to document.body — going any higher
      // here would render those popups behind this Drawer's opaque Paper:
      // technically open and interactive, but invisible.
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 3 }}
      slotProps={{
        paper: {
          sx: {
            width: DRAWER_WIDTH,
            // MUI v6 Paper renders a dark-mode "elevation overlay" as a
            // separate background-image gradient layered on top of
            // background-color (tied to the --Paper-shadow/elevation
            // level) — overriding backgroundColor alone doesn't remove it,
            // which is why this kept looking lighter than background.default
            // despite the color override. Killing the image layer directly.
            backgroundColor: "background.default",
            backgroundImage: "none",
            border: "1px solid",
            borderRight: "none",
            borderColor: "divider",
            boxShadow: (theme) => theme.shadows[16],
            display: "flex",
            flexDirection: "column",
            // Both axes must be explicitly "visible" (not just the one we care
            // about) — per the CSS overflow spec, if only one of overflow-x/
            // overflow-y is "visible" while the other isn't, the visible one is
            // silently forced to "auto" and clips anyway. Setting both avoids
            // that, letting the hide-chevron below poke outside the Paper.
            overflowX: "visible",
            overflowY: "visible",
          },
        },
      }}
    >
      <Tooltip title="Hide Dev Tools" placement="left">
        <IconButton
          onClick={() => setOpen(false)}
          aria-label="Hide Dev Tools"
          size="small"
          sx={{
            position: "absolute",
            top: CHEVRON_TOP,
            left: -CHEVRON_SIZE / 2,
            width: CHEVRON_SIZE,
            height: CHEVRON_SIZE,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            boxShadow: 1,
            zIndex: 1,
            "&:hover": { bgcolor: "background.paper", boxShadow: 3 },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 0.75,
          px: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
          minHeight: 0,
        }}
      >
        <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Dev Tools
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Tooltip title="Open as page">
            <IconButton
              component={Link}
              to="/dev-tools"
              onClick={() => setOpen(false)}
              aria-label="Open Dev Tools as page"
              size="small"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              onClick={() => setOpen(false)}
              aria-label="Close Dev Tools"
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ p: 2, overflow: "auto", flexGrow: 1, minHeight: 0 }}>
        {open && (
          <SupportServiceForm embedded initialTopLevelTab={initialTab} />
        )}
      </Box>
    </Drawer>
  );
};

export default DevToolsOverlay;
